import React, { useState, useEffect, useRef } from "react";
import { captureSubmissionError } from "../../utils/sentryUtils";
import SubmittingCard from "../../components/SubmittingCard";
import SubmissionErrorCard from "../../components/SubmissionErrorCard";

import { getReferringBranchConfig } from "./utils/ReferrerQuoteUtils";

const ReferrerQuoteSubmission = ({
  transactionType,
  defaultChannel,
  defaultReferrer,
  fallbackChannel,
  referral,
  inBranchReferral,
  agentName,
  agentEmailAddress,
  additionalInformation,
  personalDetails,
  sellingDetails,
  buyingDetails,
  googleAnalyticsId,
  campaign,
  onFlowComplete,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ref guard prevents StrictMode double-mount from firing a second request
  const hasInitiated = useRef(false);

  useEffect(() => {
    if (hasInitiated.current) return;
    hasInitiated.current = true;

    if (!personalDetails) {
      console.error("Referrer quote submission blocked: personalDetails is null");
      captureSubmissionError("ReferrerQuote", 0, "personalDetails was null at submission time");
      setError("Unable to generate your quote");
      setLoading(false);
      return;
    }

    const handleError = (err) => {
      console.error("Referrer quote submission failed:", err);
      setError("Unable to generate your quote");
      setLoading(false);
    };

    const channel = referral?.referralSource || defaultChannel;

    let referrer = referral?.referralSource ? referral.referralCode : defaultReferrer;
    // Override referrer with the branch display name, e.g. 'Brown & Merry - Berkhamsted'
    if (inBranchReferral) {
      referrer = getReferringBranchConfig(defaultReferrer).referrer;
    }

    fetch("/api/quote", {
      method: "PUT",
      body: JSON.stringify({
        transactionType,
        channel,
        fallbackChannel,
        referrer,
        agentName,
        agentEmailAddress,
        additionalInformation,
        personalDetails,
        sellingDetails: transactionType === "Selling" || transactionType === "BuyingAndSelling" ? sellingDetails : null,
        buyingDetails: transactionType === "Buying" || transactionType === "BuyingAndSelling" ? buyingDetails : null,
        remortgagingDetails: null,
        referralCode: referral ? referral.referralCode : null,
        googleAnalyticsId,
        campaign: campaign ?? null,
      }),
    })
      .then(async (resp) => {
        if (resp.ok) {
          const quoteData = await resp.json();
          setLoading(false);
          onFlowComplete({
            quoteDetails: quoteData,
          });
        } else {
          const body = await resp.text().catch(() => "");
          console.error("Referrer quote submission failed:", resp.status, body);
          captureSubmissionError("ReferrerQuote", resp.status, body);
          setError("Unable to generate your quote");
          setLoading(false);
        }
      }, handleError)
      // Catches errors thrown inside the .then() handlers (e.g. resp.json() SyntaxError)
      .catch(handleError);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- submit once on mount; ref guard prevents StrictMode re-fire

  if (loading) {
    return <SubmittingCard title="Working out your price" text="This won’t take long." />;
  }

  if (error) {
    return <SubmissionErrorCard error={error} />;
  }

  return null;
};

export default ReferrerQuoteSubmission;
